import {
  chainlinkEthUsdFeedAbi,
  uniswapV3PriceProviderV2Abi,
} from "@silo/contract-abi-types";
import {
  chainlinkEthUsdFeedAddress,
  uniswapV3PriceProviderV2Address,
  uniswapWethUsdcPoolAddress,
} from "@silo/contract-addresses";
import {
  ChainPublicClientService,
  getChainKey,
  makeUsd,
  USD_DECIMALS,
} from "@silo/types";
import { Effect, pipe } from "effect";
import { getContract } from "viem";
import { ContractsClientError } from "./types";
import { shiftDecimal } from "@silo/utils";
import { match } from "ts-pattern";

export type GetQuotePrice = ReturnType<typeof getQuotePrice>;
export type GetQuotePriceSuccess = Effect.Effect.Success<GetQuotePrice>;
export type GetQuotePriceError = Effect.Effect.Error<GetQuotePrice>;
export type GetQuotePriceContext = Effect.Effect.Context<GetQuotePrice>;

/**
 * Quote price is the price of ETH in US dollars
 */
export function getQuotePrice() {
  return Effect.gen(function* () {
    const quotePrice = yield* Effect.firstSuccessOf([
      getChainlinkQuotePrice(),
      getUniswapQuotePrice(),
    ]);

    return quotePrice;
  });
}

export type GetChainlinkQuotePrice = ReturnType<typeof getChainlinkQuotePrice>;
export type GetChainlinkQuotePriceSuccess =
  Effect.Effect.Success<GetChainlinkQuotePrice>;
export type GetChainlinkQuotePriceError =
  Effect.Effect.Error<GetChainlinkQuotePrice>;
export type GetChainlinkQuotePriceContext =
  Effect.Effect.Context<GetChainlinkQuotePrice>;

export function getChainlinkQuotePrice() {
  return Effect.gen(function* () {
    const { publicClient } = yield* ChainPublicClientService;
    const chainKey = getChainKey(publicClient.chain.id);

    const chainlinkEthUsdFeedContract = getContract({
      address: chainlinkEthUsdFeedAddress[chainKey],
      abi: chainlinkEthUsdFeedAbi,
      client: publicClient,
    });

    const { decimals, latestAnswer } = yield* Effect.tryPromise({
      try: async () => {
        const [decimals, latestAnswer] = await Promise.all([
          chainlinkEthUsdFeedContract.read.decimals(),
          chainlinkEthUsdFeedContract.read.latestAnswer(),
        ]);

        return { decimals, latestAnswer };
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    const quotePrice = pipe(
      shiftDecimal(latestAnswer, USD_DECIMALS - decimals),
      makeUsd
    );

    return quotePrice;
  });
}

export type GetUniswapQuotePrice = ReturnType<typeof getUniswapQuotePrice>;
export type GetUniswapQuotePriceSuccess =
  Effect.Effect.Success<GetUniswapQuotePrice>;
export type GetUniswapQuotePriceError =
  Effect.Effect.Error<GetUniswapQuotePrice>;
export type GetUniswapQuotePriceContext =
  Effect.Effect.Context<GetUniswapQuotePrice>;

export function getUniswapQuotePrice() {
  return Effect.gen(function* () {
    const { publicClient } = yield* ChainPublicClientService;
    const chainKey = getChainKey(publicClient.chain.id);

    const address = match(chainKey)
      .with("ethereum", () => uniswapV3PriceProviderV2Address.mainnetLegacy)
      .with("arbitrum", () => uniswapV3PriceProviderV2Address.arbitrum)
      .with("optimism", () => uniswapV3PriceProviderV2Address.optimism)
      .with("base", () => uniswapV3PriceProviderV2Address.base)
      .with("anvil", () => uniswapV3PriceProviderV2Address.anvil)
      .with("local", () => uniswapV3PriceProviderV2Address.local)
      .exhaustive();

    const uniswapV3PriceProviderV2Contract = getContract({
      address,
      abi: uniswapV3PriceProviderV2Abi,
      client: publicClient,
    });

    const poolAddress = uniswapWethUsdcPoolAddress[chainKey];

    const quotePrice = yield* Effect.tryPromise({
      try: async () => {
        const price = await uniswapV3PriceProviderV2Contract.read.quotePrice([
          poolAddress,
        ]);

        return makeUsd(price);
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    return quotePrice;
  });
}
